/**
 * The main/backup screens' pure parts (wave 7b-tail C): what the overview
 * shows in words, what each viewer may do to a production of the pair here,
 * and whether the standby agrees with the active. Unit-tested in
 * `redundancy.test.ts`; the components only render what these return.
 */
import type { PairGradeWire, PairReasonWire, SitePairProductionWire, StandbyStreamWire, ActiveStreamWire } from '@acp/api-contract/pairStatus.js';
import type { PartInstanceStateWire, PlayoutStateWire } from '@acp/api-contract/playout.js';
import type { ConnectivityState } from '../connectivity/state.js';
import { allows, type Grants } from './permissions.js';

/** A duration for the overview: `850 ms`, `12.4 s`, `3 min`. */
export function durationText(ms: number | null): string {
  if (ms === null) return '–';
  if (ms < 1_000) return `${String(Math.round(ms))} ms`;
  if (ms < 60_000) return `${(ms / 1_000).toFixed(1)} s`;
  return `${String(Math.round(ms / 60_000))} min`;
}

export const GRADE_LABEL: Readonly<Record<PairGradeWire, string>> = { ok: 'OK', warn: 'WARN', emergency: 'EMERGENCY' };

/** The badge a grade wears (the locked palette's semantic tokens, D25). */
export const GRADE_VARIANT: Readonly<Record<PairGradeWire, 'success' | 'warning' | 'destructive'>> = { ok: 'success', warn: 'warning', emergency: 'destructive' };

export const REASON_LABEL: Readonly<Record<PairReasonWire, string>> = {
  'active-lost': 'Active lost',
  isolated: 'Active isolated (muted)',
  'active-unreported': 'Active not reporting',
  'standby-missing': 'Standby missing',
  'stream-lost': 'Stream lost',
  syncing: 'Syncing',
  'witness-lost': 'Witness lost',
  'peer-link-down': 'Peer link down',
  'in-sync': 'In sync',
  'not-running': 'Not running',
  'no-pair': 'No pair',
};

/** The standby's stream in a line: `synced · lag 2 · heard 90 ms ago · snapshot 30.0 s ago · 1 gap · 0 resyncs`. */
export function streamText(stream: StandbyStreamWire | null): string | null {
  if (stream === null) return null;
  const state = stream.connected ? (stream.synced ? 'synced' : 'syncing') : 'not connected';
  const lag = stream.lagSeqs === null ? '' : ` · lag ${String(stream.lagSeqs)}`;
  const plural = (n: number, word: string): string => `${String(n)} ${word}${n === 1 ? '' : 's'}`;
  return `${state}${lag} · heard ${durationText(stream.lastHeardAgoMs)} ago · snapshot ${durationText(stream.lastSnapshotAgoMs)} ago · ${plural(stream.gaps, 'gap')} · ${plural(stream.resyncs, 'resync')}`;
}

/** The active's acks in a line: `1 standby · last ack 90 ms ago · 0 behind`. */
export function acksText(acks: ActiveStreamWire | null): string | null {
  if (acks === null) return null;
  if (acks.standbys === 0) return 'no standby following';
  return `${String(acks.standbys)} standby · last ack ${durationText(acks.lastAckAgoMs)} ago${acks.lagSeqs === null ? '' : ` · ${String(acks.lagSeqs)} behind`}`;
}

export type PairActions = {
  /** A switchover (either host: the cloud relays it to the production's node). */
  readonly switchover: boolean;
  /** Toggle `manualOnly` (node only: the pair's own setting). */
  readonly manualOnly: boolean;
  /** The emergency activation (node only, asked of the STANDBY's node, when the active is lost). */
  readonly forcePromote: boolean;
};

/**
 * What the viewer may do to one production of the overview, here. The server checks again (a node's grants are
 * unknown: everything is offered and its answer stands). `selfNodeId`: the node the page talks to, or null (the
 * cloud).
 */
export function pairActions(row: SitePairProductionWire, siteId: string, grants: Grants, selfNodeId: string | null): PairActions {
  const resource = { siteId, studioId: row.studioId };
  const switcher = allows(grants, 'node.switchover', resource);
  const onPair = row.activeNode !== null && row.standbyNode !== null;
  return {
    switchover: switcher && onPair && row.reason !== 'no-pair',
    manualOnly: switcher && selfNodeId !== null && onPair,
    forcePromote: switcher && allows(grants, 'playout.activate', resource) && selfNodeId !== null && row.standbyNode === selfNodeId && row.activeNode !== selfNodeId && row.reason === 'active-lost',
  };
}

export type Belief = { readonly onAir: string; readonly next: string; readonly key: string };

function title(part: PartInstanceStateWire | null): string {
  if (part === null) return '—';
  return part.instance.part.title === '' ? part.instance.partId : part.instance.part.title;
}

/**
 * On air and next as one process believes them. `key` compares the part INSTANCES (a standby continues from the
 * active's very instances), so two parts that share a title never read as agreement.
 */
export function beliefOf(state: PlayoutStateWire): Belief {
  return { onAir: title(state.current), next: title(state.next), key: `${state.current?.instance.instanceId ?? '-'}/${state.next?.instance.instanceId ?? '-'}` };
}

/** `agree` / `disagree`, or `unknown` without a standby state to compare. */
export function agreement(active: Belief, standby: Belief | null): 'agree' | 'disagree' | 'unknown' {
  if (standby === null) return 'unknown';
  return active.key === standby.key ? 'agree' : 'disagree';
}

/** The node the page talks to now (the connectivity manager's primary, when it is a node that answered), or null: the cloud. */
export function selfNodeIdOf(state: ConnectivityState): string | null {
  const primary = state.endpoints.find((e) => e.endpoint.id === state.primaryId);
  return primary?.endpoint.kind === 'node' ? (primary.node?.nodeId ?? null) : null;
}
